import { Check, Copy } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Explain, Hint } from './Explain';
import { cn } from '@/lib/cn';
import {
  formatMoney,
  formatTime,
  truncateMiddle,
  type TimeZoneMode,
} from '@/lib/format';
import type { CurrencyMetadataRow, SourceStatus } from '@/types';

/* --------------------------------------------------------------------------
   CopyId — support lives on copy/paste. Every id in this app is copyable, and
   long hashes are truncated for reading while copying the FULL value.
   -------------------------------------------------------------------------- */

export function CopyId({
  value,
  truncate,
  className,
  mono = true,
}: {
  value: string | number | null | undefined;
  truncate?: boolean;
  className?: string;
  mono?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  if (value === null || value === undefined || value === '') {
    return <span className="text-text-faint">—</span>;
  }

  const full = String(value);
  const shown = truncate ? truncateMiddle(full) : full;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      setTimeout(() => setCopied(false), 1100);
    } catch {
      /* clipboard blocked in some iframe contexts — fail quietly */
    }
  };

  return (
    <button
      type="button"
      onClick={copy}
      title={truncate ? full : undefined}
      className={cn(
        'group inline-flex max-w-full items-center gap-1 rounded px-1 -mx-1 text-left align-middle',
        'hover:bg-surface-raised focus-visible:bg-surface-raised',
        mono && 'font-mono',
        className,
      )}
    >
      <span className="truncate">{shown}</span>
      {copied ? (
        <Check size={11} className="shrink-0 text-ok" />
      ) : (
        <Copy
          size={11}
          className="shrink-0 text-text-faint opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        />
      )}
    </button>
  );
}

/* --------------------------------------------------------------------------
   Money — formatted for humans via currency_metadata, exact value on hover.
   Never destroy the stored precision; support sometimes needs the true number.
   -------------------------------------------------------------------------- */

export function Money({
  amount,
  meta,
  currency,
  className,
  tone,
}: {
  amount: string | number | null | undefined;
  meta?: CurrencyMetadataRow | null;
  currency?: string | null;
  className?: string;
  tone?: 'bet' | 'win' | 'rollback';
}) {
  if (amount === null || amount === undefined || amount === '') {
    return <span className="text-text-faint">—</span>;
  }

  const pretty = formatMoney(amount, meta, currency);
  const raw = String(amount);
  const toneClass =
    tone === 'win' ? 'text-ok' : tone === 'rollback' ? 'text-warn' : 'text-text';

  return (
    <Hint
      content={
        <div className="space-y-1">
          <div className="eyebrow text-text-dim">Exact stored value</div>
          <div className="font-mono text-[11.5px]">{raw}</div>
          {meta && (
            <div className="border-t border-hairline pt-1 text-[11px] text-text-dim">
              shown at {meta.display_precision} dp · {meta.rounding.replace(/_/g, ' ').toLowerCase()}
            </div>
          )}
        </div>
      }
    >
      <span className={cn('tnum cursor-help font-mono', toneClass, className)}>{pretty}</span>
    </Hint>
  );
}

/* --------------------------------------------------------------------------
   Timestamp — one zone everywhere, raw source value on hover.
   Sources disagree about wall-clock for the same instant; this is where we stop
   that from misleading anyone.
   -------------------------------------------------------------------------- */

export function Timestamp({
  date,
  raw,
  mode,
  withDate = false,
  className,
}: {
  date: Date | null;
  raw?: string | null;
  mode: TimeZoneMode;
  withDate?: boolean;
  className?: string;
}) {
  if (!date) return <span className="text-text-faint">—</span>;

  return (
    <Hint
      content={
        <div className="space-y-1">
          <div className="eyebrow text-text-dim">As stored by the source</div>
          <div className="font-mono text-[11.5px]">{raw ?? '—'}</div>
          <div className="border-t border-hairline pt-1 text-[11px] text-text-dim">
            UTC {date.toISOString().replace('T', ' ').slice(0, 19)}
            <br />
            Local {formatTime(date, 'local', true)}
          </div>
        </div>
      }
    >
      <span className={cn('tnum cursor-help font-mono text-text-dim', className)}>
        {formatTime(date, mode, withDate)}
      </span>
    </Hint>
  );
}

/* --------------------------------------------------------------------------
   SourceStatus — the four states, always distinguishable.
   "empty" and "unavailable" must never look alike: one is an answer, the other
   is the absence of one.
   -------------------------------------------------------------------------- */

export const STATUS_META: Record<
  SourceStatus,
  { glyph: string; color: string; bg: string; label: string; glossary: string }
> = {
  ok: { glyph: '●', color: 'text-ok', bg: 'bg-ok-bg', label: 'ok', glossary: 'status_ok' },
  empty: {
    glyph: '○',
    color: 'text-empty',
    bg: 'bg-empty-bg',
    label: 'empty',
    glossary: 'status_empty',
  },
  skipped: {
    glyph: '◌',
    color: 'text-skipped',
    bg: 'bg-skipped-bg',
    label: 'skipped',
    glossary: 'status_skipped',
  },
  unavailable: {
    glyph: '▲',
    color: 'text-crit',
    bg: 'bg-crit-bg',
    label: 'unavailable',
    glossary: 'status_unavailable',
  },
};

export function StatusDot({ status, className }: { status: SourceStatus; className?: string }) {
  const meta = STATUS_META[status];
  return <span className={cn('leading-none', meta.color, className)}>{meta.glyph}</span>;
}

/* -------------------------------------------------------------------------- */

export function Panel({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('border border-hairline bg-surface', className)}>{children}</div>
  );
}

export function Eyebrow({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('eyebrow', className)}>{children}</div>;
}

/** Key/value grid used by the generic renderers (per-game bets, rtp, currency). */
export function KeyValue({
  label,
  children,
  term,
}: {
  label: ReactNode;
  children: ReactNode;
  term?: string;
}) {
  return (
    <div className="flex min-w-0 items-baseline gap-2 border-b border-hairline/60 py-1 last:border-0">
      <div className="w-[42%] shrink-0 truncate text-[11.5px] text-text-dim">
        {term ? <Explain term={term}>{label}</Explain> : label}
      </div>
      <div className="min-w-0 flex-1 overflow-hidden font-mono text-[12px] text-text">
        {children}
      </div>
    </div>
  );
}
